
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";

const FEEDBACK_BUCKET = 'attachments';

export const uploadAttachment = async (file: File): Promise<string | null> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `feedback-attachments/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from(FEEDBACK_BUCKET)
      .upload(filePath, file, {
        contentType: file.type,
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Error uploading attachment: ${uploadError.message}`);
    }

    // The `attachments` bucket is private (see migration
    // 20260817170000_feedback_bucket_private.sql). getPublicUrl still returns a
    // stable, path-encoding URL string that we persist in
    // feedback_logs.attachment_url, but that URL will NOT serve the object —
    // reads must go through getFeedbackAttachmentSignedUrl below, which the
    // admin-only RLS policy authorizes for platform admins.
    const { data: { publicUrl } } = supabase
      .storage
      .from(FEEDBACK_BUCKET)
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

/**
 * Extract the in-bucket object path from a stored feedback attachment URL.
 * Historical rows store a public URL of the form
 *   https://<proj>.supabase.co/storage/v1/object/public/attachments/feedback-attachments/<uuid>.<ext>
 * and the object path we need to sign is everything after the bucket segment,
 * i.e. `feedback-attachments/<uuid>.<ext>`. If a bare path was ever stored, it
 * is returned as-is.
 */
const objectPathFromAttachmentUrl = (attachmentUrl: string): string | null => {
  if (!attachmentUrl) return null;
  const marker = `/${FEEDBACK_BUCKET}/`;
  const idx = attachmentUrl.indexOf(marker);
  if (idx !== -1) {
    return decodeURIComponent(attachmentUrl.slice(idx + marker.length));
  }
  // Fallback: value already looks like an in-bucket path.
  if (attachmentUrl.startsWith('feedback-attachments/')) {
    return attachmentUrl.replace(/^\//, '');
  }
  return null;
};

/**
 * Mint a short-lived signed URL for a feedback attachment stored in the private
 * `attachments` bucket. Access is gated by the admin-only RLS SELECT policy, so
 * this only succeeds for platform admins. Returns null if the path can't be
 * derived or signing fails.
 */
export const getFeedbackAttachmentSignedUrl = async (
  attachmentUrl: string,
  ttlSeconds = 60,
): Promise<string | null> => {
  const path = objectPathFromAttachmentUrl(attachmentUrl);
  if (!path) return null;

  const { data, error } = await supabase.storage
    .from(FEEDBACK_BUCKET)
    .createSignedUrl(path, ttlSeconds);

  if (error) {
    console.error("Error signing feedback attachment URL:", error);
    return null;
  }
  return data?.signedUrl ?? null;
};
